/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { useState, useRef, useEffect, forwardRef } from 'react'
import { useUIStore } from '../../state/uiStore'
import { IS_DEV, ENV_BADGE } from '../../config/env'
import { OnlineStatusBadge } from './OnlineStatusBadge'

/**
 * Item du menu dropdown.
 * Si separator=true, rend une ligne de séparation (les autres champs sont ignorés).
 */
export interface HeaderMenuItem {
  id: string
  label: string
  icon?: React.ReactNode
  onClick: () => void
  danger?: boolean
  separator?: boolean
}

export interface HeaderProps {
  title?: string
  centerContent?: React.ReactNode
  readingModeBadge?: {
    mode: 'silent' | 'audio' | 'italian'
    characterName?: string
    onCycle: () => void
  }
  secondLine?: {
    content: React.ReactNode
    onClick?: () => void
    disabled?: boolean
  }
  showBackButton?: boolean
  onBack?: () => void
  showLogo?: boolean
  /**
   * Items du menu dropdown.
   * Utiliser useAppMenu() pour obtenir le menu standard de l'application.
   * Les items avec separator=true rendent une ligne de séparation.
   * Le bouton Mode sombre/clair est toujours ajouté en dernier (sauf hideTheme=true).
   */
  menuItems?: HeaderMenuItem[]
  /** Masquer le bouton Mode sombre/clair */
  hideTheme?: boolean
  onSearchOpen?: () => void
  searchActive?: boolean
  className?: string
  style?: React.CSSProperties
  testId?: string
}

export const Header = forwardRef<HTMLElement, HeaderProps>(function Header(
  {
    title,
    centerContent,
    readingModeBadge,
    secondLine,
    showBackButton = false,
    onBack,
    showLogo = false,
    menuItems = [],
    hideTheme = false,
    onSearchOpen,
    searchActive = false,
    className = '',
    style,
    testId = 'header',
  },
  ref
) {
  const { toggleTheme, theme } = useUIStore()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  const handleMenuItemClick = (action: () => void) => {
    action()
    setIsMenuOpen(false)
  }

  const hasMenuItems = menuItems.length > 0 || !hideTheme

  return (
    <header
      ref={ref}
      className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 ${secondLine ? 'py-2' : 'py-0'} shadow-sm touch-none ${className}`}
      style={{ touchAction: 'none', ...style }}
      data-testid={testId}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      onTouchStart={(e: React.TouchEvent) => e.stopPropagation()}
      onTouchMove={(e: React.TouchEvent) => e.preventDefault()}
    >
      <div className="flex flex-col max-w-7xl mx-auto">
        {/* Première ligne */}
        <div className="flex items-stretch justify-between min-h-[3.5rem]">
          {/* Gauche : bouton retour OU logo OU espace vide */}
          <div className="self-stretch flex-shrink-0 flex items-stretch">
            {showBackButton && onBack ? (
              <button
                onClick={onBack}
                className="flex items-center justify-center px-2 hover:opacity-70 transition-opacity"
                aria-label="Retour"
                data-testid="back-button"
              >
                <img
                  src={`${import.meta.env.BASE_URL}icons/icon-192.png`}
                  alt="Répét"
                  className="h-full w-auto max-h-14 rounded-lg py-1"
                />
              </button>
            ) : showLogo ? (
              <div className="flex items-center justify-center px-2" data-testid="logo">
                <img
                  src={`${import.meta.env.BASE_URL}icons/icon-192.png`}
                  alt="Répét"
                  className="h-full w-auto max-h-14 rounded-lg py-1"
                />
              </div>
            ) : (
              <div className="w-10 h-14" />
            )}
          </div>

          {/* Centre : titre OU contenu personnalisé */}
          <div className="flex-1 mx-4 flex items-center justify-center min-w-0 gap-2">
            {centerContent ||
              (title && (
                <>
                  <h1
                    className="text-lg font-bold text-gray-900 dark:text-gray-100 text-center truncate"
                    data-testid="header-title"
                  >
                    {title}
                  </h1>
                  {readingModeBadge && (
                    <button
                      onClick={readingModeBadge.onCycle}
                      className={`text-xs px-3 py-1.5 rounded-full font-semibold whitespace-nowrap transition-colors cursor-pointer hover:opacity-80 ${
                        readingModeBadge.mode === 'silent'
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                          : readingModeBadge.mode === 'audio'
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                            : 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                      }`}
                      data-testid="reading-mode"
                      aria-label="Changer de méthode de lecture"
                    >
                      {readingModeBadge.mode === 'silent'
                        ? 'LECTURE'
                        : readingModeBadge.mode === 'audio'
                          ? 'LECTURE AUDIO'
                          : readingModeBadge.characterName
                            ? `ITALIENNES (${readingModeBadge.characterName.toUpperCase()})`
                            : 'ITALIENNES'}
                    </button>
                  )}
                  {IS_DEV && ENV_BADGE && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                      data-testid="env-badge"
                      title="Environnement de développement"
                    >
                      {ENV_BADGE}
                    </span>
                  )}
                </>
              ))}
          </div>

          {/* Droite : loupe + badge offline + menu */}
          <div className="flex-shrink-0 flex items-center justify-end gap-1">
            <OnlineStatusBadge className="mr-1" />
            {onSearchOpen && (
              <button
                onClick={onSearchOpen}
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                  searchActive
                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700'
                }`}
                aria-label="Rechercher dans le texte"
                aria-pressed={searchActive}
                data-testid="search-button"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-4.35-4.35M17 11A6 6 0 1 0 5 11a6 6 0 0 0 12 0z"
                  />
                </svg>
              </button>
            )}

            {hasMenuItems && (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Menu"
                  aria-expanded={isMenuOpen}
                  data-testid="menu-button"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                    />
                  </svg>
                </button>

                {isMenuOpen && (
                  <div
                    className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50"
                    data-testid="dropdown-menu"
                  >
                    {/* Items personnalisés (useAppMenu) */}
                    {menuItems.map((item) =>
                      item.separator ? (
                        <div
                          key={item.id}
                          className="my-1 border-t border-gray-200 dark:border-gray-700"
                        />
                      ) : (
                        <button
                          key={item.id}
                          onClick={() => handleMenuItemClick(item.onClick)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                            item.danger
                              ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                          data-testid={`menu-${item.id}`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </button>
                      )
                    )}

                    {/* Mode sombre/clair — toujours en dernier */}
                    {!hideTheme && (
                      <button
                        onClick={() => handleMenuItemClick(toggleTheme)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        data-testid="menu-theme"
                      >
                        {theme === 'light' ? (
                          <>
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                              />
                            </svg>
                            <span>Mode sombre</span>
                          </>
                        ) : (
                          <>
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                              />
                            </svg>
                            <span>Mode clair</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Seconde ligne (optionnelle) */}
        {secondLine && (
          <div className="flex items-center justify-center mt-1 mb-1">
            {secondLine.onClick ? (
              <button
                onClick={secondLine.onClick}
                disabled={secondLine.disabled}
                className="text-sm italic text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="header-second-line"
              >
                {secondLine.content}
              </button>
            ) : (
              <div
                className="text-sm italic text-gray-600 dark:text-gray-400"
                data-testid="header-second-line"
              >
                {secondLine.content}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  )
})
